import { useEffect, useRef, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { useDebouncedValue } from '../../../lib/useDebouncedValue'

export interface TypeaheadOption {
  value: string
  label: string
  secondary?: string | undefined
}

export interface TypeaheadPickerProps {
  id: string
  label: string
  placeholder?: string
  value: string | undefined
  /** The known display label for `value` (e.g. resolved from a prior search) — shown in the input
   * instead of the raw id/code when the caller has one. */
  valueLabel?: string | undefined
  onChange: (value: string | undefined, optionLabel: string | undefined) => void
  search: (term: string, signal: AbortSignal) => Promise<TypeaheadOption[]>
  minChars?: number
}

/**
 * A real ARIA-combobox dimension picker backed by an existing list endpoint (`listPrograms` /
 * `listAccounts` / `listLegalEntities` — no new backend route this slice, IB-311). Replaces a raw-id
 * text input with type-to-search + a result listbox; the picker degrades honestly to "No matches" if
 * `search` throws (e.g. the caller's tenant/capability can't reach that list) rather than pretending
 * nothing is wrong — `filterSearchSources.ts` documents that trade-off per source.
 */
export function TypeaheadPicker({ id, label, placeholder, value, valueLabel, onChange, search, minChars = 2 }: TypeaheadPickerProps) {
  const [term, setTerm] = useState(valueLabel ?? value ?? '')
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<TypeaheadOption[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const debouncedTerm = useDebouncedValue(term, 250)
  const containerRef = useRef<HTMLDivElement>(null)
  const listboxId = `${id}-listbox`

  useEffect(() => {
    const trimmed = debouncedTerm.trim()
    if (trimmed.length < minChars) {
      setOptions([])
      return
    }
    const controller = new AbortController()
    setLoading(true)
    search(trimmed, controller.signal)
      .then((results) => {
        setOptions(results)
        setActiveIndex(-1)
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [debouncedTerm, minChars, search])

  useEffect(() => {
    function onDocumentPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocumentPointerDown)
    return () => document.removeEventListener('mousedown', onDocumentPointerDown)
  }, [])

  function selectOption(option: TypeaheadOption) {
    setTerm(option.label)
    setOpen(false)
    onChange(option.value, option.label)
  }

  function clear() {
    setTerm('')
    setOptions([])
    onChange(undefined, undefined)
  }

  const trimmed = debouncedTerm.trim()
  const showListbox = open && (loading || options.length > 0 || trimmed.length >= minChars)

  return (
    <div className="iw-field" ref={containerRef}>
      <label className="iw-field-label" htmlFor={id}>
        {label}
      </label>
      <div className="iw-typeahead">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${id}-opt-${activeIndex}` : undefined}
          className="iw-input"
          placeholder={placeholder}
          value={term}
          onChange={(e) => {
            const next = e.target.value
            setTerm(next)
            setOpen(true)
            if (next.trim() === '' && value) onChange(undefined, undefined)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActiveIndex((i) => Math.min(i + 1, options.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActiveIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
              e.preventDefault()
              selectOption(options[activeIndex])
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
        />
        {value ? (
          <button type="button" className="iw-typeahead__clear" onClick={clear} aria-label={`Clear ${label}`}>
            <Icon name="x" size={12} />
          </button>
        ) : null}
        {showListbox ? (
          <ul id={listboxId} role="listbox" aria-label={label} className="iw-typeahead__listbox">
            {loading ? (
              <li className="iw-typeahead__hint">Searching…</li>
            ) : options.length === 0 ? (
              <li className="iw-typeahead__hint">No matches for "{trimmed}".</li>
            ) : (
              options.map((option, index) => (
                <li
                  key={option.value}
                  id={`${id}-opt-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`iw-typeahead__option${index === activeIndex ? ' iw-typeahead__option--active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    selectOption(option)
                  }}
                >
                  <span>{option.label}</span>
                  {option.secondary ? <span className="iw-typeahead__secondary">{option.secondary}</span> : null}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
